import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "./api";
import { BlockedUsers } from "./components/BlockedUsers";
import { Chat } from "./components/Chat";
import { Chats } from "./components/Chats";
import { Discovery } from "./components/Discovery";
import { DiscoveryFilters } from "./components/DiscoveryFilters";
import { Likes } from "./components/Likes";
import { Matches } from "./components/Matches";
import { NotificationCenter } from "./components/NotificationCenter";
import { NotificationSettings } from "./components/NotificationSettings";
import { Onboarding } from "./components/Onboarding";
import { Profile } from "./components/Profile";
import { ProfileExtras } from "./components/ProfileExtras";
import { initializeTelegram } from "./telegram";
import type { ConversationItem, MatchItem, Me } from "./types";
import { useBackNavigation } from "./use-back-navigation";

type MainTab = "discover" | "likes" | "matches" | "chats" | "profile";
type Tab = MainTab | "chat";

function NavIcon({ name }: { name: MainTab }) {
  if (name === "discover")
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="9" y="3" width="10" height="15" rx="2" transform="rotate(12 14 11)" />
        <path d="M7 7 4 8a2 2 0 0 0-1 2l3 11a2 2 0 0 0 2 1l6-2" />
      </svg>
    );
  if (name === "likes")
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 21C7.5 17.4 3 14 3 9.5A5 5 0 0 1 12 6a5 5 0 0 1 9 3.5c0 4.5-4.5 7.9-9 11.5Z" />
      </svg>
    );
  if (name === "matches")
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="8" cy="8" r="3" />
        <circle cx="16" cy="8" r="3" />
        <path d="M3.5 19c.5-3.2 2.2-5 4.5-5s4 1.8 4.5 5" />
        <path d="M11.5 19c.5-3.2 2.2-5 4.5-5s4 1.8 4.5 5" />
      </svg>
    );
  if (name === "chats")
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M5 18.5 3 21l.7-4A8 8 0 1 1 5 18.5Z" />
      </svg>
    );
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 21c.7-4.5 3.2-7 7.5-7s6.8 2.5 7.5 7" />
    </svg>
  );
}

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [tab, setTab] = useState<Tab>("discover");
  const [chatOrigin, setChatOrigin] = useState<MainTab>("chats");
  const [selectedConversation, setSelectedConversation] = useState<ConversationItem | null>(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationSettingsOpen, setNotificationSettingsOpen] = useState(false);
  const closeNotificationSettings = useCallback(() => setNotificationSettingsOpen(false), []);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [blockedUsersOpen, setBlockedUsersOpen] = useState(false);
  const [premiumOpen, setPremiumOpen] = useState(false);
  const closeBlockedUsers = useCallback(() => setBlockedUsersOpen(false), []);
  const [filterVersion, setFilterVersion] = useState(0);
  const closeFilters = useCallback(() => setFiltersOpen(false), []);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [state, setState] = useState<"booting" | "ready" | "telegram-required" | "restricted" | "deleted" | "error">(
    "booting",
  );

  const refreshMe = useCallback(async () => {
    const next = await api<Me>("/v1/me");
    setMe(next);
    setState("ready");
  }, []);

  const refreshUnread = useCallback(async () => {
    const result = await api<{ unreadCount: number }>("/v1/notifications/unread-count");
    setUnreadNotifications(result.unreadCount);
  }, []);

  useEffect(() => {
    const webApp = initializeTelegram();
    if (!webApp?.initData) {
      setState("telegram-required");
      return;
    }
    void (async () => {
      try {
        await api("/v1/auth/telegram", { method: "POST", body: JSON.stringify({ initData: webApp.initData }) });
        await refreshMe();
      } catch (error) {
        if (error instanceof ApiError && error.code === "account_restricted") {
          setState("restricted");
          return;
        }
        if (error instanceof ApiError && error.code === "account_deleted") {
          setState("deleted");
          return;
        }
        setState("error");
      }
    })();
  }, [refreshMe]);

  const notificationsEnabled = state === "ready" && Boolean(me?.onboardingComplete);

  useEffect(() => {
    if (!notificationsEnabled) return;

    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      void refreshUnread().catch(() => undefined);
      void api("/v1/me/presence", { method: "POST" }).catch(() => undefined);
    };
    refresh();

    const interval = window.setInterval(refresh, 30_000);
    const handleVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [notificationsEnabled, refreshUnread]);

  const openConversation = (conversation: ConversationItem) => {
    setChatOrigin(tab === "matches" ? "matches" : "chats");
    setSelectedConversation(conversation);
    setTab("chat");
  };

  const openMatch = (match: MatchItem) => {
    openConversation({
      id: match.conversationId,
      matchId: match.id,
      user: match.user,
      lastMessage: null,
      unreadCount: 0,
      createdAt: match.createdAt,
    });
  };

  const navigate = (next: MainTab) => {
    setSelectedConversation(null);
    setTab(next);
  };

  useBackNavigation(() => navigate("chats"), tab === "matches");

  if (state === "booting")
    return (
      <main className="boot-screen">
        <div className="brand-mark">D</div>
        <div className="boot-line">
          <span />
        </div>
      </main>
    );

  if (state === "telegram-required")
    return (
      <main className="system-screen">
        <div className="brand-mark">D</div>
        <h1>Откройте DonDate в Telegram</h1>
        <p>Перейдите в приложение через нашего Telegram-бота, чтобы начать знакомиться.</p>
      </main>
    );

  if (state === "deleted")
    return (
      <main className="system-screen">
        <div className="brand-mark">D</div>
        <h1>Аккаунт удалён</h1>
        <p>Профиль и данные общения больше недоступны в DonDate.</p>
      </main>
    );

  if (state === "restricted")
    return (
      <main className="system-screen">
        <div className="brand-mark">D</div>
        <h1>Доступ ограничен</h1>
        <p>Повторная авторизация станет доступна после снятия ограничения модерацией.</p>
      </main>
    );

  if (state === "error" || !me)
    return (
      <main className="system-screen">
        <div className="brand-mark">D</div>
        <h1>Не удалось войти</h1>
        <p>Закройте Mini App, откройте её снова и повторите попытку.</p>
        <button type="button" className="secondary-button" onClick={() => location.reload()}>
          Повторить
        </button>
      </main>
    );

  if (!me.onboardingComplete) return <Onboarding initialProfile={me.profile} onComplete={refreshMe} />;

  if (tab === "chat" && selectedConversation) {
    return (
      <div className="app-shell chat-app-shell">
        <Chat
          conversation={selectedConversation}
          onBack={() => {
            setSelectedConversation(null);
            setTab(chatOrigin);
          }}
        />
      </div>
    );
  }

  const activeTab = tab as MainTab;

  return (
    <div className="app-shell">
      <header className="topbar">
        <button type="button" className="wordmark" aria-label="Открыть анкеты" onClick={() => navigate("discover")}>
          Don<span>Date</span>
        </button>
        <div className="topbar-actions">
          <span className="safety-dot">18+</span>
          <button
            type="button"
            className="notification-button"
            aria-label={unreadNotifications > 0 ? `Уведомления, непрочитанных: ${unreadNotifications}` : "Уведомления"}
            onClick={() => setNotificationsOpen(true)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" />
            </svg>
            {unreadNotifications > 0 && (
              <span className="notification-count" aria-hidden="true">
                {unreadNotifications > 99 ? "99+" : unreadNotifications}
              </span>
            )}
          </button>
        </div>
      </header>

      <main className="screen">
        <div className="screen-view" key={activeTab}>
          {activeTab === "discover" &&
            (me.profile?.isDiscoverable === false ? (
              <section className="review-gate" aria-label="Анкета скрыта">
                <span className="review-icon" aria-hidden="true">
                  ◌
                </span>
                <span className="eyebrow">Пауза знакомств</span>
                <h2>Ваша анкета скрыта</h2>
                <p>
                  Включите видимость в профиле, чтобы снова смотреть анкеты и ставить лайки. Переписки остаются
                  доступными.
                </p>
                <button type="button" className="secondary-button" onClick={() => navigate("profile")}>
                  Настройки профиля
                </button>
              </section>
            ) : (
              <Discovery
                key={filterVersion}
                onMatch={() => navigate("matches")}
                onFilters={() => setFiltersOpen(true)}
                onPremium={() => setPremiumOpen(true)}
              />
            ))}
          {activeTab === "likes" && (
            <Likes isDiscoverable={me.profile?.isDiscoverable ?? true} onMatched={() => navigate("matches")} />
          )}
          {activeTab === "matches" && (
            <>
              <button type="button" className="matches-back" onClick={() => navigate("chats")}>
                ← Чаты
              </button>
              <Matches onOpen={openMatch} />
            </>
          )}
          {activeTab === "chats" && <Chats onOpen={openConversation} onMatches={() => navigate("matches")} />}
          {activeTab === "profile" && (
            <Profile
              me={me}
              onRefresh={refreshMe}
              onOpenNotifications={() => setNotificationsOpen(true)}
              onOpenNotificationSettings={() => setNotificationSettingsOpen(true)}
              onOpenFilters={() => setFiltersOpen(true)}
              onOpenBlockedUsers={() => setBlockedUsersOpen(true)}
              onDeleted={() => {
                setNotificationsOpen(false);
                setSelectedConversation(null);
                setMe(null);
                setState("deleted");
              }}
            />
          )}
        </div>
      </main>

      <nav className="bottom-nav" aria-label="Основная навигация">
        {(
          [
            ["chats", "Чаты"],
            ["likes", "Лайки"],
            ["discover", "Анкеты"],
            ["profile", "Профиль"],
          ] as const
        ).map(([name, label]) => (
          <button
            type="button"
            key={name}
            className={activeTab === name ? "active" : ""}
            aria-current={activeTab === name ? "page" : undefined}
            onClick={() => navigate(name)}
          >
            <span className="nav-icon">
              <NavIcon name={name} />
            </span>
            <span>{label}</span>
          </button>
        ))}
      </nav>

      {premiumOpen && <ProfileExtras page="premium" onClose={() => setPremiumOpen(false)} />}
      {blockedUsersOpen && <BlockedUsers onClose={closeBlockedUsers} />}
      {filtersOpen && (
        <DiscoveryFilters onClose={closeFilters} onApplied={() => setFilterVersion((current) => current + 1)} />
      )}
      {notificationsOpen && (
        <NotificationCenter
          onClose={() => setNotificationsOpen(false)}
          onUnreadChanged={refreshUnread}
          onSettings={() => {
            setNotificationSettingsOpen(true);
          }}
          onNavigate={(nextTab) => {
            setNotificationsOpen(false);
            navigate(nextTab);
          }}
        />
      )}
      {notificationSettingsOpen && <NotificationSettings onClose={closeNotificationSettings} />}
    </div>
  );
}
